<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import TTooltip from '../TTooltip.vue';
import { useIsTouchDevice } from '../useIsTouchDevice';
import type { EvaluationIconProps } from './evaluation-icon';
import {
  evaluationIconDimensions,
  evaluationIconStateContent,
  evaluationIconStateStyles,
} from './evaluation-icon';

const props = withDefaults(defineProps<EvaluationIconProps>(), {
  size: 'md',
});

const emits = defineEmits<{
  click: [event: MouseEvent];
}>();

const { isTouch } = useIsTouchDevice();

const instance = getCurrentInstance();
const isInteractive = computed(() => instance?.vnode.props?.onClick != null);

const effectiveTooltip = computed(() => {
  if (isInteractive.value && isTouch.value) {
    return undefined;
  }

  return props.tooltip;
});

const dimensions = computed(() => evaluationIconDimensions[props.size]);
const stateStyles = computed(() => evaluationIconStateStyles[props.state]);
const stateContent = computed(() =>
  props.state === 'notAnswered'
    ? null
    : evaluationIconStateContent[props.state],
);

const containerClasses = computed(() => [
  dimensions.value.container,
  stateStyles.value.container,
  isInteractive.value ? stateStyles.value.interactiveContainer : null,
]);

const rootClasses = computed(() =>
  isInteractive.value
    ? 'relative inline-flex shrink-0 cursor-pointer rounded-full focus-visible:ring-primary-500 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none'
    : 'relative inline-flex shrink-0',
);

const handleClick = (event: MouseEvent): void => {
  emits('click', event);
};
</script>

<template>
  <TTooltip :content="effectiveTooltip" placement="top">
    <component
      :is="isInteractive ? 'button' : 'span'"
      :type="isInteractive ? 'button' : undefined"
      :class="rootClasses"
      :role="isInteractive ? undefined : 'img'"
      :aria-label="props.ariaLabel"
      @click="isInteractive ? handleClick($event) : undefined"
    >
      <span
        class="flex items-center justify-center rounded-full"
        :class="containerClasses"
      >
        <HugeIcon
          v-if="stateContent"
          :icon="stateContent.icon"
          :size="dimensions.iconSize"
        />
      </span>
    </component>
  </TTooltip>
</template>
